import { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface RichEditorProps {
  value: string;
  onChange: (val: string) => void;
}

type FontSize = '12px' | '14px' | '16px' | '18px' | '20px' | '24px' | '28px' | '32px';
type Align = 'left' | 'center' | 'right' | 'justify';

const FONT_SIZES: FontSize[] = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

const RichEditor = ({ value, onChange }: RichEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlText, setHtmlText] = useState(value);
  const [fontSize, setFontSize] = useState<FontSize>('14px');
  const [showInsertImg, setShowInsertImg] = useState(false);
  const [imgUrl, setImgUrl] = useState('');
  const [imgUploading, setImgUploading] = useState(false);
  const [imgUploadError, setImgUploadError] = useState('');
  const imgFileRef = useRef<HTMLInputElement>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const savedRange = useRef<Range | null>(null);

  // 初始化内容：组件挂载时 或 value 从外部变化时（编辑模式重新打开）同步到编辑器
  // 用 Symbol 作哨兵，确保第一次传入任何值（包括空字符串）都能触发初始化
  const lastValueRef = useRef<string>('\x00__UNINITIALIZED__\x00');
  useEffect(() => {
    if (editorRef.current && !isHtmlMode) {
      if (value !== lastValueRef.current) {
        editorRef.current.innerHTML = value;
        lastValueRef.current = value;
        setHtmlText(value);
      }
    }
  }, [value, isHtmlMode]);

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreRange = () => {
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const exec = (cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    syncContent();
  };

  const syncContent = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      lastValueRef.current = html;
      onChangeRef.current(html);
      setHtmlText(html);
    }
  }, []);

  const toggleHtmlMode = () => {
    if (!isHtmlMode) {
      // 切换到 HTML 模式
      const html = editorRef.current?.innerHTML ?? '';
      setHtmlText(html);
      setIsHtmlMode(true);
    } else {
      // 切换回可视模式
      setIsHtmlMode(false);
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = htmlText;
          onChange(htmlText);
        }
      }, 0);
    }
  };

  const handleHtmlChange = (val: string) => {
    setHtmlText(val);
    onChange(val);
  };

  const handleFontSize = (size: FontSize) => {
    setFontSize(size);
    exec('fontSize', '7');
    // 替换 font size 7 为实际 px
    if (editorRef.current) {
      const fonts = editorRef.current.querySelectorAll('font[size="7"]');
      fonts.forEach((f) => {
        const span = document.createElement('span');
        span.style.fontSize = size;
        span.innerHTML = (f as HTMLElement).innerHTML;
        f.replaceWith(span);
      });
      syncContent();
    }
  };

  const handleInsertImage = (url: string) => {
    if (!url.trim()) return;
    restoreRange();
    exec('insertHTML', `<img src="${url.trim()}" style="max-width:100%;height:auto;display:block;margin:8px 0;" alt="商品图片" />`);
    setImgUrl('');
    setShowInsertImg(false);
  };

  const handleImgFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setImgUploadError('请选择图片文件'); return; }
    if (file.size > 10 * 1024 * 1024) { setImgUploadError('图片不能超过 10MB'); return; }
    setImgUploadError('');
    setImgUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const fileName = `detail/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('images').upload(fileName, file, { upsert: false, contentType: file.type });
      if (uploadError) { setImgUploadError('上传失败，请重试'); return; }
      const { data } = supabase.storage.from('images').getPublicUrl(fileName);
      handleInsertImage(data.publicUrl);
    } catch {
      setImgUploadError('上传失败，请重试');
    } finally {
      setImgUploading(false);
      if (imgFileRef.current) imgFileRef.current.value = '';
    }
  };

  const handleColor = (color: string) => {
    restoreRange();
    exec('foreColor', color);
    setShowColorPicker(false);
  };

  const PRESET_COLORS = [
    '#000000', '#333333', '#666666', '#999999', '#cccccc', '#ffffff',
    '#e53e3e', '#dd6b20', '#d69e2e', '#38a169', '#3182ce', '#805ad5',
    '#f56565', '#ed8936', '#ecc94b', '#48bb78', '#4299e1', '#9f7aea',
  ];

  const toolbarBtnCls = 'w-7 h-7 flex items-center justify-center rounded cursor-pointer hover:bg-gray-100 transition-colors text-gray-600 text-sm';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* ── 工具栏 ── */}
      <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex flex-wrap items-center gap-1">
        {/* HTML 模式切换 */}
        <button
          type="button"
          onClick={toggleHtmlMode}
          className={`px-2 py-1 rounded text-xs font-mono font-bold cursor-pointer whitespace-nowrap transition-colors ${
            isHtmlMode ? 'bg-rose-100 text-rose-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          HTML
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* 撤销/重做 */}
        <button type="button" onClick={() => exec('undo')} className={toolbarBtnCls} title="撤销">
          <i className="ri-arrow-go-back-line" />
        </button>
        <button type="button" onClick={() => exec('redo')} className={toolbarBtnCls} title="重做">
          <i className="ri-arrow-go-forward-line" />
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* 加粗/斜体/下划线/删除线 */}
        <button type="button" onClick={() => exec('bold')} className={toolbarBtnCls} title="加粗">
          <i className="ri-bold" />
        </button>
        <button type="button" onClick={() => exec('italic')} className={toolbarBtnCls} title="斜体">
          <i className="ri-italic" />
        </button>
        <button type="button" onClick={() => exec('underline')} className={toolbarBtnCls} title="下划线">
          <i className="ri-underline" />
        </button>
        <button type="button" onClick={() => exec('strikeThrough')} className={toolbarBtnCls} title="删除线">
          <i className="ri-strikethrough" />
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* 字体大小 */}
        <select
          value={fontSize}
          onChange={(e) => handleFontSize(e.target.value as FontSize)}
          className="h-7 border border-gray-200 rounded text-xs px-1 outline-none bg-white cursor-pointer"
        >
          {FONT_SIZES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* 文字颜色 */}
        <div className="relative">
          <button
            type="button"
            onClick={() => { saveRange(); setShowColorPicker((v) => !v); setShowInsertImg(false); }}
            className={`${toolbarBtnCls} flex-col gap-0`}
            title="文字颜色"
          >
            <span className="text-xs font-bold leading-none">A</span>
            <span className="w-4 h-1 rounded-sm bg-rose-500 mt-0.5" />
          </button>
          {showColorPicker && (
            <div className="absolute top-8 left-0 z-20 bg-white border border-gray-200 rounded-xl p-3 shadow-lg w-44">
              <div className="grid grid-cols-6 gap-1.5">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => handleColor(c)}
                    className="w-6 h-6 rounded cursor-pointer border border-gray-100 hover:scale-110 transition-transform"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 背景色 */}
        <button type="button" onClick={() => exec('hiliteColor', '#fef08a')} className={toolbarBtnCls} title="背景高亮">
          <i className="ri-mark-pen-line" />
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* 对齐 */}
        <button type="button" onClick={() => exec('justifyLeft')} className={toolbarBtnCls} title="左对齐">
          <i className="ri-align-left" />
        </button>
        <button type="button" onClick={() => exec('justifyCenter')} className={toolbarBtnCls} title="居中">
          <i className="ri-align-center" />
        </button>
        <button type="button" onClick={() => exec('justifyRight')} className={toolbarBtnCls} title="右对齐">
          <i className="ri-align-right" />
        </button>
        <button type="button" onClick={() => exec('justifyFull')} className={toolbarBtnCls} title="两端对齐">
          <i className="ri-align-justify" />
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* 列表 */}
        <button type="button" onClick={() => exec('insertUnorderedList')} className={toolbarBtnCls} title="无序列表">
          <i className="ri-list-unordered" />
        </button>
        <button type="button" onClick={() => exec('insertOrderedList')} className={toolbarBtnCls} title="有序列表">
          <i className="ri-list-ordered" />
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* 插入图片 */}
        <div className="relative">
          <button
            type="button"
            onClick={() => { saveRange(); setShowInsertImg((v) => !v); setShowColorPicker(false); }}
            className={toolbarBtnCls}
            title="插入图片"
          >
            <i className="ri-image-add-line" />
          </button>
          {showInsertImg && (
            <div className="absolute top-8 right-0 z-20 bg-white border border-gray-200 rounded-xl p-3 shadow-lg w-64">
              <p className="text-xs font-medium text-gray-700 mb-3">插入图片</p>
              {/* 本地上传 */}
              <button
                type="button"
                onClick={() => imgFileRef.current?.click()}
                disabled={imgUploading}
                className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-rose-300 hover:text-rose-500 cursor-pointer transition-all disabled:opacity-50 whitespace-nowrap mb-2"
              >
                {imgUploading ? (
                  <><div className="w-4 h-4 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />上传中...</>
                ) : (
                  <><i className="ri-upload-cloud-line text-base" />点击上传本地图片</>
                )}
              </button>
              {imgUploadError && <p className="text-xs text-rose-500 mb-2">{imgUploadError}</p>}
              {/* 分隔线 */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-400">或输入链接</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <input
                type="text"
                value={imgUrl}
                onChange={(e) => setImgUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInsertImage(imgUrl); } }}
                placeholder="https://..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-400 mb-2"
              />
              <div className="flex gap-2">
                <button type="button" onClick={() => { setShowInsertImg(false); setImgUploadError(''); }} className="flex-1 border border-gray-200 text-gray-500 rounded-lg py-1.5 text-xs cursor-pointer whitespace-nowrap">取消</button>
                <button type="button" onClick={() => handleInsertImage(imgUrl)} disabled={!imgUrl.trim()} className="flex-1 bg-rose-500 text-white rounded-lg py-1.5 text-xs cursor-pointer whitespace-nowrap disabled:opacity-40">插入链接</button>
              </div>
              <input ref={imgFileRef} type="file" accept="image/*" className="hidden" onChange={handleImgFileChange} />
            </div>
          )}
        </div>

        {/* 清除格式 */}
        <button type="button" onClick={() => exec('removeFormat')} className={toolbarBtnCls} title="清除格式">
          <i className="ri-format-clear" />
        </button>
      </div>

      {/* ── 编辑区 ── */}
      {isHtmlMode ? (
        <textarea
          value={htmlText}
          onChange={(e) => handleHtmlChange(e.target.value)}
          className="w-full px-4 py-3 text-sm font-mono outline-none resize-none text-gray-700 bg-gray-50"
          style={{ minHeight: 360 }}
          placeholder="在此输入 HTML 代码..."
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={syncContent}
          onBlur={syncContent}
          className="px-4 py-3 text-sm text-gray-700 outline-none"
          style={{ minHeight: 360, lineHeight: 1.8 }}
          data-placeholder="请输入商品详情，支持图文混排..."
        />
      )}

      {/* 字数提示 */}
      <div className="bg-gray-50 border-t border-gray-100 px-4 py-1.5 flex items-center justify-between">
        <span className="text-xs text-gray-400">支持图文混排，点击 HTML 可切换源码编辑模式</span>
        <span className="text-xs text-gray-400">{isHtmlMode ? htmlText.length : (editorRef.current?.innerText?.length ?? 0)} 字符</span>
      </div>
    </div>
  );
};

export default RichEditor;
